<script setup lang="ts">
import {resolveRelations, resolveMediaProps} from '~/utils/relations'

const route = useRoute()
const localePath = useLocalePath()
const {
  public: {baseUrl},
} = useRuntimeConfig()

const projectFetch = useProjects()

const {data: rawProject} = await useAsyncData(`project:${route.params.slug}`, () =>
  projectFetch.fetchBySlug(route.params.slug as string)
)

// Live preview wraps only the project document; nextSlug/previousSlug/meta come from rawItem
const {data: draftProject} = useLivePreview({initialData: rawProject.value?.project ?? null, depth: 2})
const data = computed(() =>
  rawProject.value ? {...rawProject.value, project: draftProject.value ?? rawProject.value.project} : null
)

if (!rawProject.value?.project) {
  throw showError({
    statusCode: 404,
  })
}

const seo = data.value?.project?.meta

useSeoMeta({
  title: seo?.title ?? data.value?.project?.title,
  description: seo?.description || data.value?.meta?.description,
  ogUrl: `${baseUrl}/projects/${route.params.industry}/${route.params.slug}`,
  ogTitle: seo?.ogTitle || seo?.title || data.value?.project?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image ?? data.value?.project?.image),
  ogImageAlt: resolveMediaAlt(seo?.image ?? data.value?.project?.image),
})
</script>

<template>
  <div>
    <Container v-if="data?.project" class="screen-content px-0 pb-8">
      <LayoutPageHeader :title="data.project.title" :country="data.project.country?.countryName">
        <template #eyebrow>
          <span>
            {{
              resolveRelations(data.project.industries)
                .map(i => i.name)
                .join(', ') + (data.project.country?.countryName ? ' | ' : '')
            }}
            {{ data.project.country?.countryName ?? '' }}
          </span>
        </template>

        <template #description>
          {{ data.project.description }}
        </template>
      </LayoutPageHeader>

      <div class="px-2">
        <div class="mx-auto mb-8 max-w-screen-lg rounded-lg bg-white p-2 bordershadow-medium">
          <NuxtImg
            fit="cover"
            class="mx-auto aspect-video w-full rounded object-cover bordershadow-gray"
            v-bind="resolveMediaProps(data.project.image)"
            :width="600"
            :height="400"
          />
        </div>
      </div>

      <div class="flex w-full flex-col gap-y-16 px-8 md:px-16 lg:px-0">
        <ProjectSpecialSection id="overview" :title="data.project.overviewTitle || $t('projects.overview')">
          <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:w-2/3">
            <ProjectOverviewItem :title="$t('projects.solution')" :body="data.project.solution" />
            <ProjectOverviewItem :title="$t('projects.duration')" :body="data.project.duration" />
            <ProjectOverviewItem :title="$t('projects.team')" :body="data.project.team" />
            <ProjectOverviewItem :title="$t('projects.pricingModel')" :body="data.project.pricingModel" />
          </div>
        </ProjectSpecialSection>

        <RichText v-if="data.project.overview" :raw="data.project.overview" class="mx-auto" />

        <ProjectSpecialSection
          v-if="data.project.technologies?.length"
          id="technologies"
          :title="$t('projects.built_with')"
        >
          <div
            class="mt-4 grid grid-cols-2 flex-row flex-wrap items-center justify-center gap-x-7 gap-y-6 lg:flex lg:gap-4 lg:px-8"
          >
            <div v-for="(tech, techIdx) of data.project.technologies" :key="tech.id" class="h-28 w-28">
              <ProjectTechItem :tech="tech" />
            </div>
          </div>
        </ProjectSpecialSection>

        <ProjectSpecialSection
          v-if="data.project.highlights?.length"
          id="highlights"
          :title="$t('projects.key_highlights')"
        >
          <div class="mx-auto grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:w-2/3">
            <div
              v-for="highlight of data.project.highlights"
              :key="highlight.highlight ?? undefined"
              class="w-full rounded-lg bg-gray-100 p-4"
            >
              <p class="text-lg font-bold">
                {{ highlight.highlight }}
              </p>
            </div>
          </div>
        </ProjectSpecialSection>

        <section class="w-full">
          <RichText :raw="data.project.content" class="mx-auto" />
        </section>

        <ProjectSpecialSection v-if="data.project.features?.length" id="features" :title="$t('projects.features')">
          <div class="mx-auto flex w-full flex-wrap justify-center md:w-2/3">
            <ProjectFeature
              v-for="feature in data.project.features"
              :key="feature.id"
              class="mb-8 w-1/2 px-4 lg:w-1/4"
              :functionality="feature"
            />
          </div>
        </ProjectSpecialSection>

        <ProjectSpecialSection v-if="data.project.testimonial" id="testimonial" :title="$t('projects.testimonial')">
          <TestimonialCard class="mx-auto w-full md:w-2/3" :testimonial="data.project.testimonial" />
        </ProjectSpecialSection>

        <section>
          <div class="mx-auto flex w-full justify-between md:w-2/3">
            <NuxtLink
              v-if="data.previousSlug"
              :to="localePath(`/projects/${route.params.industry}/${data.previousSlug}`)"
              class="group mr-1 mt-4 flex items-center gap-x-2 py-3 font-medium text-gray-700 md:justify-start"
            >
              <Icon name="lucide:move-left" size="18" class="transition group-hover:-translate-x-1" />
              {{ $t('projects.previous') }}
            </NuxtLink>

            <NuxtLink
              v-if="data.nextSlug"
              :to="localePath(`/projects/${route.params.industry}/${data.nextSlug}`)"
              class="group ml-auto mt-4 flex items-center gap-x-2 py-3 font-medium text-gray-700 md:justify-end"
            >
              {{ $t('projects.next') }}
              <Icon name="lucide:move-right" size="18" class="transition group-hover:translate-x-1" />
            </NuxtLink>
          </div>
        </section>
      </div>
    </Container>
    <Cta :href="localePath('/contact')" :label="$t('home.contact.ctaLabel')" class="!pb-32" />
  </div>
</template>
